import { _js } from '@ifixit/utils/localize';
import { captureException, SentryError } from '@ifixit/sentry';
import { FaIcon } from '@ifixit/ui/components/icon';
import { ToggleGroup, ToggleGroupItem } from '@ifixit/ui/components/toggle-group';
import { cn } from '@ifixit/ui/lib/utils';
import { useEffect, useRef, useState } from 'react';
import { callAjaxIO } from 'Shared/ajax_io';
import { requireAuth } from 'Shared/require-auth';
import { FIELD_HINT_CLASS, SECTION_HEADING_CLASS } from './field';
import { IconActionButton } from './IconActionButton';
import { SuggestInput } from './SuggestInput';
import { TOGGLE_PILL_CLASS } from './toggle_pill';
import { suggestTags } from './suggest_api';
import { type TagsApi, useTags } from './tags_api';

/**
 * Metadata sections shared by the wiki and guide editors. Wiki tags persist
 * immediately through the v2 endpoints; guide tags remain controlled so the
 * page can save them with its other metadata.
 */

export type PermissionLevel = {
   threshold: number;
   /** Active users clearing the threshold, or null where a count is noise
    *  (everyone / staff only). */
   users: number | null;
};

export function EditPermissionsSection({
   maxAllowed,
   maxDisplayed,
   levels,
   helpUrl,
   value,
   onChange,
}: {
   maxAllowed: number;
   maxDisplayed: number;
   /** PatrolLib::THRESHOLD_LEVELS with cached approver counts. */
   levels: PermissionLevel[];
   helpUrl: string;
   value: number;
   onChange: (threshold: number) => void;
}) {
   // The infinite sentinel arrives clamped to the display max, so that
   // value is the "Staff only" pill; the save flow translates it back to
   // 'inf'. That pill's threshold doubles as its React key and Radix value,
   // so it must not collide with a real PatrolLib::THRESHOLD_LEVELS tier —
   // unreachable today (maxDisplayed is hardcoded well above the highest
   // tier). Report it and drop the duplicate rather than throwing: a render
   // throw has no boundary above it here, so it would blank the whole editor
   // (losing unsaved edits) over what is otherwise one redundant pill.
   const collidesWithStaffPill = levels.some(level => level.threshold === maxDisplayed);
   useEffect(() => {
      if (collidesWithStaffPill) {
         captureException(
            new SentryError(
               `EditPermissionsSection: maxDisplayed (${maxDisplayed}) collides with a THRESHOLD_LEVELS tier`
            )
         );
      }
   }, [collidesWithStaffPill, maxDisplayed]);

   const options = [
      ...levels
         .filter(level => level.threshold !== maxDisplayed)
         .map(level => ({
            ...level,
            label: level.threshold === 0 ? _js('Everyone') : `${level.threshold.toLocaleString()}+`,
         })),
      { threshold: maxDisplayed, users: null, label: _js('Staff only') },
   ];
   const selected = options.find(option => option.threshold === value);

   return (
      <div className="flex w-full flex-col gap-2">
         <div className="mb-2 flex items-center gap-2">
            <h2 className={SECTION_HEADING_CLASS}>{_js('Permissions')}</h2>
            <a
               href={helpUrl}
               target="_blank"
               rel="noreferrer"
               className="text-gray-400 hover:text-gray-600"
               aria-label={_js('About editing permissions')}
            >
               <FaIcon name="circle-question" className="size-4" aria-hidden />
            </a>
         </div>
         <ToggleGroup
            type="single"
            value={selected ? String(selected.threshold) : ''}
            // Radix maps a single-select ToggleGroup to `group`, which leaves
            // its `radio` children without the parent that gives them "1 of N".
            role="radiogroup"
            aria-label={_js('Reputation required to approve changes')}
            onValueChange={next => {
               // Radix reports a re-click of the active item as ''; a page
               // always has a threshold, so ignore the deselect.
               if (next !== '') {
                  onChange(Number(next));
               }
            }}
            className="flex flex-wrap gap-2"
         >
            {options.map(option => (
               <ToggleGroupItem
                  key={option.threshold}
                  value={String(option.threshold)}
                  // A non-privileged editor can't raise the bar past their
                  // own reputation, matching what saveThreshold accepts.
                  disabled={option.threshold > maxAllowed}
                  className={TOGGLE_PILL_CLASS}
               >
                  {option.label}
               </ToggleGroupItem>
            ))}
         </ToggleGroup>
         <p className={cn('mt-1', FIELD_HINT_CLASS)}>
            {levelDescription(selected, value, maxDisplayed)}
         </p>
      </div>
   );
}

function levelDescription(
   selected: PermissionLevel | undefined,
   value: number,
   maxDisplayed: number
): string {
   if (!selected) {
      // A stored threshold between levels (set before the picker, or via
      // the v1 slider). Leave it visible instead of silently snapping.
      return _js(
         'Custom level: requires %1 reputation to approve changes.',
         value.toLocaleString()
      );
   }
   if (selected.threshold === 0) {
      return _js('Anyone signed in can make changes without approval.');
   }
   if (selected.threshold >= maxDisplayed) {
      return _js('Only admins and moderators can approve changes.');
   }
   if (selected.users !== null) {
      return _js(
         'Requires %1+ reputation to approve changes. ≈ %2 members qualify.',
         selected.threshold.toLocaleString(),
         selected.users.toLocaleString()
      );
   }
   return _js('Requires %1+ reputation to approve changes.', selected.threshold.toLocaleString());
}

type ControlledTagsProps = {
   maxLength: number;
   value: string[];
   onChange: (tags: string[]) => void;
};

type PersistentTagsProps = {
   maxLength: number;
   tagsApi: TagsApi;
   initialTags: string[];
   disabled?: boolean;
};

export function TagsSection(props: ControlledTagsProps | PersistentTagsProps) {
   return 'tagsApi' in props ? (
      <PersistentTagsSection {...props} />
   ) : (
      <ControlledTagsSection {...props} />
   );
}

function PersistentTagsSection({
   maxLength,
   tagsApi,
   initialTags,
   disabled = false,
}: PersistentTagsProps) {
   const [query, setQuery] = useState('');
   const { tags, addTag, removeTag, isError } = useTags(tagsApi, initialTags);

   const commit = (raw: string) => {
      if (disabled) {
         return;
      }
      // Each pill writes immediately, so a logged-out visitor needs a
      // session before the optimistic add can mean anything; the typed text
      // stays in the field while the login prompt is up (and after a
      // cancel), so nothing is lost either way.
      requireAuth({
         message: _js('Log in to edit tags.'),
         onAuthorize: () => {
            // The endpoint takes one canonical tag per call, so split a comma
            // list here and add each new tag once; already-present tags are
            // skipped (the Set catches "foo, foo", which `tags` alone
            // wouldn't — the query cache doesn't reflect the first add until
            // its onMutate runs).
            const newTags = new Set(raw.split(',').map(part => part.trim()));
            for (const tag of newTags) {
               if (tag && !tags.includes(tag)) {
                  addTag(tag);
               }
            }
            setQuery('');
         },
      });
   };

   return (
      <TagsField
         maxLength={maxLength}
         tags={tags}
         query={query}
         setQuery={setQuery}
         commit={commit}
         removeTag={tag =>
            requireAuth({
               message: _js('Log in to edit tags.'),
               onAuthorize: () => removeTag(tag),
            })
         }
         // Adds apply optimistically (useTags), so the field never locks.
         readOnly={false}
         disabled={disabled}
         error={isError ? _js('Could not update tags. Please try again.') : null}
      />
   );
}

function ControlledTagsSection({ maxLength, value, onChange }: ControlledTagsProps) {
   const [query, setQuery] = useState('');
   const [isAdding, setIsAdding] = useState(false);
   const [error, setError] = useState<string | null>(null);
   // The addTags roundtrip is async, so merge against the tags as they stand
   // when it resolves instead of resurrecting a tag removed mid-flight.
   const valueRef = useRef(value);
   useEffect(() => {
      valueRef.current = value;
   }, [value]);
   const queuedCommitsRef = useRef<string[]>([]);
   const isAddingRef = useRef(false);

   const commitInput = (raw: string) => {
      const trimmed = raw.trim().replace(/,+$/, '');
      if (!trimmed) {
         return;
      }
      if (isAddingRef.current) {
         queuedCommitsRef.current.push(trimmed);
         return;
      }
      isAddingRef.current = true;
      setIsAdding(true);
      setError(null);
      callAjaxIO<string[]>('addTags', trimmed)
         .then(parsed => {
            onChange([...new Set([...valueRef.current, ...parsed])]);
            setQuery('');
         })
         .catch(() => setError(_js('Could not add that tag. Please try again.')))
         .finally(() => {
            isAddingRef.current = false;
            setIsAdding(false);
            const queued = queuedCommitsRef.current.shift();
            if (queued !== undefined) {
               commitInput(queued);
            }
         });
   };

   return (
      <TagsField
         maxLength={maxLength}
         tags={value}
         query={query}
         setQuery={setQuery}
         commit={commitInput}
         removeTag={tag => onChange(value.filter(current => current !== tag))}
         readOnly={isAdding}
         error={error}
      />
   );
}

function TagsField({
   maxLength,
   tags,
   query,
   setQuery,
   commit,
   removeTag,
   readOnly,
   disabled = false,
   error,
}: {
   maxLength: number;
   tags: string[];
   query: string;
   setQuery: (query: string) => void;
   commit: (tag: string) => void;
   removeTag: (tag: string) => void;
   readOnly: boolean;
   disabled?: boolean;
   error: string | null;
}) {
   return (
      <div className="flex w-full flex-col gap-2">
         <h2 className={cn('mb-1', SECTION_HEADING_CLASS)}>{_js('Tags')}</h2>
         {tags.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1.5">
               {tags.map(tag => (
                  <span
                     key={tag}
                     className="inline-flex items-center gap-1 rounded-full border border-gray-300 bg-gray-100 py-0.5 pl-2.5 pr-1.5 text-sm"
                  >
                     <span className="max-w-56 truncate">{tag}</span>
                     <IconActionButton
                        label={_js('Remove tag %1', tag)}
                        icon="xmark"
                        disabled={disabled}
                        onClick={() => removeTag(tag)}
                        className="text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
                     />
                  </span>
               ))}
            </div>
         )}
         <div className="relative">
            <SuggestInput
               queryKey="editor-tag-suggest"
               value={query}
               onValueChange={setQuery}
               onSelect={suggestion => commit(suggestion.label)}
               fetchSuggestions={suggestTags}
               placeholder={_js('Add a tag...')}
               ariaLabel={_js('Add a tag')}
               maxLength={maxLength}
               excludeKeys={tags}
               readOnly={readOnly || disabled}
               onKeyDown={event => {
                  if (disabled) {
                     return;
                  }
                  if (event.key === 'Enter' || event.key === ',') {
                     event.preventDefault();
                     commit(query);
                  } else if (event.key === 'Backspace' && query === '' && tags.length > 0) {
                     removeTag(tags.at(-1) as string);
                  }
               }}
            />
            {readOnly && (
               <FaIcon
                  name="spinner"
                  className="pointer-events-none absolute right-3 top-2.5 size-4 animate-spin text-gray-400 motion-reduce:animate-none"
                  aria-hidden
               />
            )}
         </div>
         <span aria-live="polite" className="sr-only">
            {readOnly ? _js('Adding tag…') : ''}
         </span>
         {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
         <p className={cn('mt-1', FIELD_HINT_CLASS)}>{_js('Press Enter to add a tag.')}</p>
      </div>
   );
}
